import { useRef } from "react";
import { useLegendState as useState } from "@/hooks/useLegendState";
import { useQueryClient } from "@tanstack/react-query";
import { Upload, FileText, Loader2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { useToast } from "@/hooks/use-toast";
import { knowledgeBaseService } from "@/services/knowledgeBaseService";
import { useWorkspace } from "@/contexts/WorkspaceContext";
import { extractTextFromBlob } from "@/utils/textExtractionHelper";

interface FileUploadToNoteProps {
    /** Space ID to link the notes to (optional) */
    spaceId?: string | null;
    /** Folder ID to place the notes in (optional) */
    folderId?: string | null;
    /** Topic ID to link the notes to (optional) */
    topicId?: string | null;
    /** Callback when upload completes */
    onComplete?: () => void;
    /** Whether to show as a button or inline */
    variant?: "button" | "inline";
    /** Button label */
    buttonLabel?: string;
    /** Whether multiple files are allowed */
    multiple?: boolean;
}

export function FileUploadToNote({
    spaceId = null,
    folderId = null,
    topicId = null,
    onComplete,
    variant = "button",
    buttonLabel = "Upload Files",
    multiple = true,
}: FileUploadToNoteProps) {
    const [isUploading, setIsUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState<{
        total: number;
        completed: number;
        current: string;
        failed: string[];
    } | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const { toast } = useToast();
    const queryClient = useQueryClient();
    const { currentWorkspaceId } = useWorkspace();

    const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files;
        if (!files || files.length === 0) return;

        const fileArray = Array.from(files);
        
        // Validate files - check for text-based document types
        const validFiles = fileArray.filter((file) => {
            const fileName = file.name.toLowerCase();
            const mimeType = file.type.toLowerCase();
            
            // Check if it's a supported text-based document
            const isSupported = 
                mimeType === 'application/pdf' ||
                mimeType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
                mimeType === 'application/msword' ||
                mimeType.startsWith('text/') ||
                mimeType === 'application/json' ||
                mimeType === 'text/csv' ||
                fileName.endsWith('.pdf') ||
                fileName.endsWith('.docx') ||
                fileName.endsWith('.doc') ||
                fileName.endsWith('.txt') ||
                fileName.endsWith('.md') ||
                fileName.endsWith('.markdown') ||
                fileName.endsWith('.json') ||
                fileName.endsWith('.csv');
            
            return isSupported;
        });

        if (validFiles.length === 0) {
            toast({
                title: "No valid files",
                description: "Please select text-based documents (PDF, DOCX, TXT, MD, JSON, CSV, etc.)",
                variant: "destructive",
            });
            if (fileInputRef.current) fileInputRef.current.value = "";
            return;
        }

        // Start upload process
        setIsUploading(true);
        setUploadProgress({
            total: validFiles.length,
            completed: 0,
            current: '',
            failed: [],
        });

        // Helper function to extract folder path from file's folderPath (if using Google Drive structure)
        const getFolderPathFromFile = (folderPath: string | undefined): string | null => {
            if (!folderPath || !folderPath.trim()) return null;
            return folderPath.trim();
        };

        // Extract all unique folder paths from files (if any)
        const extractFolderPaths = (files: File[]): string[] => {
            const folderPaths = new Set<string>();
            // For direct file uploads, we don't have folder paths
            // This is mainly for Google Drive integration compatibility
            return Array.from(folderPaths);
        };

        // Create folder hierarchy if needed (for Google Drive compatibility)
        const createFolderHierarchy = async (folderPaths: string[]): Promise<Map<string, string>> => {
            const folderMap = new Map<string, string>();
            // For direct uploads, we typically don't have folder paths
            // This is kept for consistency with Google Drive picker logic
            return folderMap;
        };

        const folderPaths = extractFolderPaths(validFiles);
        let folderPathToIdMap: Map<string, string> = new Map();
        
        if (folderPaths.length > 0) {
            try {
                folderPathToIdMap = await createFolderHierarchy(folderPaths);
                queryClient.invalidateQueries({ queryKey: ["/api/library-folders"] });
            } catch (error: any) {
                console.error('Error creating folder hierarchy:', error);
            }
        }

        // Process each file
        let successCount = 0;
        let failCount = 0;

        for (let i = 0; i < validFiles.length; i++) {
            const file = validFiles[i];
            const filename = file.name;

            setUploadProgress(prev => prev ? {
                ...prev,
                current: `Processing: ${filename}`,
                completed: i,
            } : null);

            try {
                // Extract text from the file
                const blob = new Blob([file], { type: file.type });
                const extracted = await extractTextFromBlob(
                    blob,
                    filename,
                    file.type || "application/octet-stream"
                );

                if (extracted.error || !extracted.text || extracted.text.trim().length === 0) {
                    throw new Error(extracted.error || "No text could be extracted from this file");
                }

                // Get folder ID if we have folder path (for Google Drive compatibility)
                const fileFolderPath = null; // Direct uploads don't have folder paths
                const fileFolderId = fileFolderPath 
                    ? folderPathToIdMap.get(fileFolderPath) || folderId
                    : folderId;

                // Create note from extracted text
                await knowledgeBaseService.createKnowledgeBaseItem({
                    title: filename.replace(/\.[^/.]+$/, ""), // Remove file extension
                    content: extracted.text,
                    type: 'note',
                    spaceId: spaceId,
                    topicId: topicId,
                    tagIds: [],
                    folderId: fileFolderId,
                }, currentWorkspaceId ?? null);

                successCount++;
                
                // Invalidate queries to refresh UI
                queryClient.invalidateQueries({ 
                    predicate: (query) => {
                        const key = query.queryKey[0];
                        return typeof key === 'string' && (
                            key === '/api/knowledge-base' || 
                            key === '/api/knowledgebase' ||
                            key === 'knowledgebase'
                        );
                    }
                });
            } catch (error: any) {
                console.error(`Error processing file ${filename}:`, error);
                failCount++;
                setUploadProgress(prev => prev ? {
                    ...prev,
                    failed: [...prev.failed, filename],
                } : null);
            }
        }

        // Update final progress
        setUploadProgress(prev => prev ? {
            ...prev,
            completed: validFiles.length,
            current: 'Complete!',
        } : null);

        // Show completion message
        if (successCount > 0) {
            let locationMessage = '';
            if (spaceId) {
                locationMessage = ` in space`;
            } else if (folderId) {
                locationMessage = ` in folder`;
            }
            toast({
                title: "Notes created",
                description: `${successCount} note(s) created successfully${locationMessage}${failCount > 0 ? `. ${failCount} failed.` : '.'}`,
            });
        }

        if (failCount > 0 && successCount === 0) {
            toast({
                title: "Upload failed",
                description: `Failed to process ${failCount} file(s).`,
                variant: "destructive",
            });
        }

        // Reset and cleanup
        setTimeout(() => {
            setIsUploading(false);
            setUploadProgress(null);
            if (fileInputRef.current) fileInputRef.current.value = "";
            if (onComplete) onComplete();
        }, 2000);
    };

    if (variant === "inline") {
        return (
            <div className="space-y-2">
                <input
                    ref={fileInputRef}
                    type="file"
                    multiple={multiple}
                    accept=".pdf,.docx,.doc,.txt,.md,.markdown,.json,.csv,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/msword,text/*,application/json,text/csv"
                    className="hidden"
                    onChange={handleFileSelect}
                    disabled={isUploading}
                />
                <div className="border-2 border-dashed rounded-lg p-8 text-center">
                    <Upload className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
                    <p className="text-sm text-muted-foreground mb-2">
                        Drag and drop files here, or click to browse
                    </p>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={isUploading}
                    >
                        {isUploading ? (
                            <>
                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                Processing...
                            </>
                        ) : (
                            <>
                                <FileText className="h-4 w-4 mr-2" />
                                Choose Files
                            </>
                        )}
                    </Button>
                </div>
                {isUploading && uploadProgress && (
                    <div className="space-y-2 p-4 border rounded-lg bg-muted/30">
                        <div className="flex items-center justify-between text-sm">
                            <span className="font-medium">Processing files...</span>
                            <span className="text-muted-foreground">
                                {uploadProgress.completed} / {uploadProgress.total}
                            </span>
                        </div>
                        <Progress
                            value={(uploadProgress.completed / uploadProgress.total) * 100}
                            className="h-2"
                        />
                        <p className="text-xs text-muted-foreground truncate">
                            {uploadProgress.current}
                        </p>
                        {uploadProgress.failed.length > 0 && (
                            <p className="text-xs text-destructive">
                                Failed: {uploadProgress.failed.length} file(s)
                            </p>
                        )}
                        <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
                            <Loader2 className="h-4 w-4 animate-spin" />
                            <span>Please wait...</span>
                        </div>
                    </div>
                )}
            </div>
        );
    }

    return (
        <>
            <input
                ref={fileInputRef}
                type="file"
                multiple={multiple}
                accept=".pdf,.docx,.doc,.txt,.md,.markdown,.json,.csv,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/msword,text/*,application/json,text/csv"
                className="hidden"
                onChange={handleFileSelect}
                disabled={isUploading}
            />
            <Button
                variant="outline"
                size="sm"
                onClick={() => fileInputRef.current?.click()}
                disabled={isUploading}
                className="w-full justify-start"
            >
                {isUploading ? (
                    <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Processing...
                    </>
                ) : (
                    <>
                        <Upload className="h-4 w-4 mr-2" />
                        {buttonLabel}
                    </>
                )}
            </Button>
            {isUploading && uploadProgress && (
                <div className="space-y-2 p-4 border rounded-lg bg-muted/30 mt-2">
                    <div className="flex items-center justify-between text-sm">
                        <span className="font-medium">Processing files...</span>
                        <span className="text-muted-foreground">
                            {uploadProgress.completed} / {uploadProgress.total}
                        </span>
                    </div>
                    <Progress
                        value={(uploadProgress.completed / uploadProgress.total) * 100}
                        className="h-2"
                    />
                    <p className="text-xs text-muted-foreground truncate">
                        {uploadProgress.current}
                    </p>
                    {uploadProgress.failed.length > 0 && (
                        <p className="text-xs text-destructive">
                            Failed: {uploadProgress.failed.length} file(s)
                        </p>
                    )}
                </div>
            )}
        </>
    );
}
